import camelcaseKeys from 'camelcase-keys';
import qs from 'qs';

import { HttpStatusCodes } from '../../_types/http-code';
import { config } from '../../config';

import {
  getTokenRemainingTime,
  getTokens,
  handleLogout,
  notifyLogout,
  refreshSession,
} from './auth';

const DEFAULT_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

function isCmsUrl(url: string) {
  const requestUrl = new URL(url, window.location.origin);

  return Object.values(config().CMS_API).some((baseUrl) => {
    const cmsUrl = new URL(baseUrl, window.location.origin);
    const basePath = cmsUrl.pathname.replace(/\/+$/, '');

    return (
      requestUrl.origin === cmsUrl.origin &&
      (requestUrl.pathname === basePath || requestUrl.pathname.startsWith(`${basePath}/`))
    );
  });
}

function parseJSON(response: Response, camelCaseConversion: boolean) {
  if (
    response.status === HttpStatusCodes.NoContent ||
    response.status === HttpStatusCodes.ResetContent
  ) {
    return null;
  }

  if (isJsonResponse(response)) {
    return response.json().then((json) => {
      if (camelCaseConversion) {
        return camelcaseKeys(json, { deep: true });
      }

      return json;
    });
  }

  return response.text();
}

function isJsonResponse(response: Response) {
  const contentType = response.headers.get('content-type');

  return (
    contentType &&
    (contentType.includes('application/json') || contentType.includes('application/hal+json'))
  );
}

interface Request {
  url: string;
  options: Omit<RequestInit, 'body'> & {
    body?: Record<string, unknown> | string | Record<string, unknown>[] | any[] | BodyInit;
  };
  auth?: boolean;
  camelCaseConversion?: boolean;
}

export interface NetworkError extends Error {
  response: any;
  status: number;
}

export default async function request<T>({
  url,
  options = {},
  auth = true,
  camelCaseConversion = true,
}: Request): Promise<T> {
  const tokens = getTokens();
  const managesCmsSession = auth && isCmsUrl(url);

  if (!tokens.accessToken && managesCmsSession) {
    notifyLogout();
    handleLogout();
    throw new Error('Unauthorized');
  }

  if (managesCmsSession && getTokenRemainingTime({ inMinutes: true }) < 1) {
    await refreshSession();
  }

  const { headers, ...restOptions } = options;
  const isFormData = options?.body instanceof FormData;

  const requestOptions = {
    method: 'GET',
    headers: {
      ...(auth
        ? {
            Authorization: `Bearer ${tokens.accessToken}`,
          }
        : {}),
      // Don't set default headers for FormData, browser will set correct Content-Type with boundary
      ...(isFormData ? {} : (headers ?? DEFAULT_HEADERS)),
    },
    ...restOptions,
  };

  if (options?.body) {
    // If body is FormData, use it directly without stringify
    if (isFormData) {
      requestOptions.body = options.body as FormData;
    } else {
      requestOptions.body = auth
        ? JSON.stringify(options.body.valueOf())
        : qs.stringify(options.body.valueOf());
    }
  }

  return fetch(url, requestOptions as RequestInit).then(async (response) => {
    if (!response.ok) {
      if (managesCmsSession && response.status === HttpStatusCodes.Unauthorized) {
        const refreshed = await refreshSession();

        if (refreshed) return request<T>({ url, options, auth, camelCaseConversion });
      }

      let errorResponse = {};
      if (isJsonResponse(response)) {
        const r = await response.json();
        errorResponse = r;
      } else {
        const r = await response.text();
        errorResponse = { error: r };
      }
      const error = new Error() as NetworkError;
      error.response = errorResponse;
      error.status = response.status;

      throw error;
    }

    return parseJSON(response, camelCaseConversion);
  });
}
